import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  limit
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject 
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { db, storage, auth } from '../firebaseConfig';
import { secureLog } from '../../lib/errors/logger';
import { isValidDocumentId, validateString } from '../../utils/validation';
import { sanitizeError } from '../../utils/sanitization';

// NOTE: This service handles FALSE PRODUCT REPORTS only (counterfeit/fake products)
// For general user reports (bugs, issues, feature requests), use src/features/reports/reportService.ts
// Do not mix general reports with product reports - they serve different purposes

export interface FalseProductReport {
  id?: string;
  productId: string;
  reportedBy: string;
  reason: 'Fake Product' | 'Wrong Description' | 'Counterfeit Brand' | 'Inappropriate Image' | 'Others';
  description: string;
  timestamp: any;
  imageUrl?: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: any;
  adminNotes?: string;
}

export interface ReportFormData {
  productId: string;
  reason: FalseProductReport['reason'];
  description: string;
  imageFile?: File;
}

export const reportService = {
  // Submit a new false product report
  async submitReport(reportData: ReportFormData, userId: string): Promise<string> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(reportData.productId)) {
        throw new Error('Invalid product ID');
      }

      if (!isValidDocumentId(userId)) {
        throw new Error('Invalid user ID');
      }

      const descriptionValidation = validateString(reportData.description, 'Description', 1, 2000);
      if (!descriptionValidation.valid) {
        throw new Error(descriptionValidation.message || 'Invalid description');
      }

      // Security: Validate reason
      const validReasons: FalseProductReport['reason'][] = ['Fake Product', 'Wrong Description', 'Counterfeit Brand', 'Inappropriate Image', 'Others'];
      if (!validReasons.includes(reportData.reason)) {
        throw new Error('Invalid report reason');
      }

      // Security: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      if (userId !== currentUser.uid) {
        // SECURITY: Prevent IDOR - users can only submit reports for themselves
        secureLog('warn', 'IDOR attempt detected in submitReport');
        throw new Error('Access denied');
      }

      // Security: Validate file if provided
      if (reportData.imageFile) {
        // Validate file size (max 5MB)
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        if (reportData.imageFile.size > MAX_FILE_SIZE) {
          throw new Error('Image file too large. Maximum size is 5MB.');
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(reportData.imageFile.type)) {
          throw new Error('Invalid image type. Only JPEG, PNG, and WebP are allowed.');
        }
      }

      let imageUrl: string | undefined;

      // Upload image if provided
      if (reportData.imageFile) {
        const imageRef = ref(storage, `false-product-reports/${userId}/${Date.now()}_${reportData.imageFile.name}`);
        const snapshot = await uploadBytes(imageRef, reportData.imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      // Create report document
      const reportRef = collection(db, 'falseProductReports');
      const reportDoc = {
        productId: reportData.productId,
        reportedBy: currentUser.uid, // Ensure reportedBy matches authenticated user
        reason: reportData.reason,
        description: reportData.description.slice(0, 2000), // Limit description length
        timestamp: serverTimestamp(),
        imageUrl: imageUrl,
        status: 'pending' as const
      };

      const docRef = await addDoc(reportRef, reportDoc);
      
      // Log product trace
      const { traceService } = await import('./productTraceService');
      await traceService.logProductTrace(
        reportData.productId,
        'REPORTED',
        userId,
        'buyer',
        `Product reported: ${reportData.reason} - ${reportData.description}`,
        {
          reportId: docRef.id,
          reason: reportData.reason
        }
      );
      
      return docRef.id;
    } catch (error) {
      secureLog('warn', 'Failed to submit report:', sanitizeError(error));
      throw new Error('Failed to submit report. Please try again.');
    }
  },

  // Get reports by user
  async getReportsByUser(userId: string): Promise<FalseProductReport[]> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(userId)) {
        return [];
      }

      // Security: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      if (userId !== currentUser.uid) {
        // SECURITY: Prevent IDOR - users can only access their own reports
        secureLog('warn', 'IDOR attempt detected in getReportsByUser');
        throw new Error('Access denied');
      }

      const reportsRef = collection(db, 'falseProductReports');
      const q = query(
        reportsRef,
        where('reportedBy', '==', userId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FalseProductReport[];
    } catch (error) {
      secureLog('warn', 'Failed to fetch reports by user:', sanitizeError(error));
      throw new Error('Failed to fetch reports.');
    }
  },

  // Get all reports (admin only)
  async getAllReports(): Promise<FalseProductReport[]> {
    try {
      // Security: Admin check - this should be done server-side, but add client-side check
      const currentUser = auth.currentUser;
      if (!currentUser) {
        secureLog('warn', 'Unauthenticated access attempt to getAllReports');
        throw new Error('Authentication required');
      }

      // Note: Full admin check should be done server-side via Firestore rules
      const reportsRef = collection(db, 'falseProductReports');
      const q = query(
        reportsRef,
        orderBy('timestamp', 'desc'),
        limit(1000) // Security: Limit results to prevent abuse
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FalseProductReport[];
    } catch (error) {
      secureLog('warn', 'Failed to fetch all reports:', sanitizeError(error));
      throw new Error('Failed to fetch reports.');
    }
  },

  // Get report by ID
  async getReportById(reportId: string): Promise<FalseProductReport | null> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(reportId)) {
        return null;
      }

      // Security: IDOR Protection - Verify user owns the report or is admin
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      const reportRef = doc(db, 'falseProductReports', reportId);
      const reportDoc = await getDoc(reportRef);
      
      if (reportDoc.exists()) {
        const reportData = reportDoc.data() as FalseProductReport;
        
        // SECURITY: Prevent IDOR - users can only access their own reports
        if (reportData.reportedBy !== currentUser.uid) {
          secureLog('warn', 'IDOR attempt detected in getReportById');
          throw new Error('Access denied');
        }

        return {
          id: reportDoc.id,
          ...reportData
        } as FalseProductReport;
      }
      
      return null;
    } catch (error) {
      secureLog('warn', 'Failed to fetch report by ID:', sanitizeError(error));
      throw new Error('Failed to fetch report details.');
    }
  },

  // Update report status (admin only)
  async updateReportStatus(reportId: string, status: FalseProductReport['status'], adminId: string, notes?: string): Promise<void> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(reportId)) {
        throw new Error('Invalid report ID');
      }

      if (!isValidDocumentId(adminId)) {
        throw new Error('Invalid admin ID');
      }

      // Security: Validate status
      const validStatuses: FalseProductReport['status'][] = ['pending', 'reviewed', 'resolved', 'rejected'];
      if (!validStatuses.includes(status)) {
        throw new Error('Invalid report status');
      }

      // Security: IDOR Protection - Verify adminId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      if (adminId !== currentUser.uid) {
        // SECURITY: Prevent IDOR - admins can only update reports as themselves
        secureLog('warn', 'IDOR attempt detected in updateReportStatus');
        throw new Error('Access denied');
      }

      // Security: Sanitize notes
      const sanitizedNotes = notes ? notes.slice(0, 1000) : undefined;

      const reportRef = doc(db, 'falseProductReports', reportId);
      await updateDoc(reportRef, {
        status: status,
        reviewedBy: currentUser.uid, // Ensure reviewedBy matches authenticated user
        reviewedAt: serverTimestamp(),
        adminNotes: sanitizedNotes
      });
    } catch (error) {
      secureLog('warn', 'Failed to update report status:', sanitizeError(error));
      throw new Error('Failed to update report status.');
    }
  },

  // Delete report image from storage
  async deleteReportImage(imageUrl: string): Promise<void> {
    try {
      // Security: Input validation
      if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.length === 0) {
        return; // Silently fail for invalid URLs
      }

      // SECURITY: Extract storage path from download URL
      // imageUrl is a full HTTPS URL (from getDownloadURL), not a storage path
      let storagePath: string | null = null;
      try {
        if (imageUrl.startsWith("https://firebasestorage.googleapis.com") || 
            imageUrl.startsWith("https://firebasestorage.app")) {
          const urlObj = new URL(imageUrl);
          const pathMatch = urlObj.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)/);
          if (pathMatch && pathMatch[1]) {
            storagePath = decodeURIComponent(pathMatch[1]);
          }
        } else if (imageUrl.startsWith("gs://")) {
          // Handle gs:// URLs
          storagePath = imageUrl.replace("gs://", "").split("/").slice(1).join("/");
        } else if (!imageUrl.includes("/") && !imageUrl.includes("://")) {
          // If it's already a storage path (legacy format)
          storagePath = imageUrl;
        }
      } catch (error) {
        secureLog('warn', 'Failed to extract storage path from imageUrl', { error: sanitizeError(error) });
        return; // Silently fail if extraction fails
      }
      
      // SECURITY: Validate extracted storage path
      if (!storagePath) {
        secureLog('warn', 'Invalid image URL format in deleteReportImage');
        return;
      }
      
      // SECURITY: Ensure path is in reports directory (prevent path traversal)
      if (!storagePath.startsWith("reports/")) {
        secureLog('warn', 'Storage path does not belong to reports directory', { storagePath });
        return; // Silently fail if path doesn't match expected directory
      }
      
      // SECURITY: Final path validation
      if (storagePath.includes("..") || storagePath.includes("//") || storagePath.length > 500) {
        secureLog('warn', 'Path traversal detected in storage path', { storagePath });
        return; // Silently fail if path traversal detected
      }

      const imageRef = ref(storage, storagePath);
      await deleteObject(imageRef);
    } catch (error) {
      secureLog('warn', 'Failed to delete report image:', sanitizeError(error));
      // Don't throw error as this is not critical
    }
  }
};
